/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { AuthenticatedTemplate, useMsal, UnauthenticatedTemplate } from '@azure/msal-react';
import { PageLayout } from '../components/PageLayout';

import '../styles/App.css';
import Login from '../components/login';
import { loginRequest } from '../authConfig';
import Chatbot from '../components/chatbot';
import { useEffect, useState } from 'react';
import {
    InteractionRequiredAuthError,
    InteractionStatus,
  } from "@azure/msal-browser";

const MainContent = () => {
    /**
     * useMsal is a hook that returns the PublicClientApplication instance.
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
     */
    const { instance, inProgress, accounts } = useMsal();
    const [apiData, setApiData] = useState(null);

    const handleLoginRedirect = async () => {
        await instance.initialize();
        instance.loginRedirect(loginRequest).catch(() => {}
        // console.log(error)
        );
    };

    const callApi = (accessToken) => 
        fetch('https://graph.microsoft.com/v1.0/me', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        });

    useEffect(() => {
        const accessTokenRequest = {
            scopes: ["user.read"],
            account: accounts[0],
        };

        if (!apiData && inProgress === InteractionStatus.None) {
            instance
                .acquireTokenSilent(accessTokenRequest)
                .then((accessTokenResponse) => {
                    // Acquire token silent success
                    let accessToken = accessTokenResponse.accessToken;
                    // Call your API with token
                    callApi(accessToken).then((response) => {
                        // console.log("response", response);
                        setApiData(response);
                    });
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    // console.log(error);
                });
        }
    }, [instance, accounts, inProgress, apiData]);

    const activeAccount = instance.getActiveAccount();

    /**
     * Most applications will need to conditionally render certain components based on whether a user is signed in or not.
     * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will
     * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
     */
    return (
        <PageLayout>
            <AuthenticatedTemplate>
                {activeAccount ? (
                    <Chatbot username={activeAccount.username && activeAccount.username.length ? activeAccount.username : activeAccount.homeAccountId} />
                ) : null}
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <Login login={handleLoginRedirect} />
            </UnauthenticatedTemplate>
        </PageLayout>
    );
};

export default MainContent;
